// Customer Suggestions API Service
import apiWithAuth from '../../services/apiWithAuth';

const BASE_URL = '/admin-panel/suggestions';

class SuggestionService {
  // Fetch suggestions with filters and pagination
  static async getSuggestions(params = {}) {
    try {
      // Filter out empty string values to avoid validation errors
      const cleanParams = Object.entries({
        page: '1',
        limit: '10',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...params
      }).reduce((acc, [key, value]) => {
        // Only include non-empty string values and non-null values
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const queryParams = new URLSearchParams(cleanParams);

      const response = await apiWithAuth.get(`${BASE_URL}?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      throw error;
    }
  }

  // Get suggestion by ID
  static async getSuggestionById(id) {
    try {
      const response = await apiWithAuth.get(`${BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching suggestion details:', error);
      throw error;
    }
  }

  // Update suggestion
  static async updateSuggestion(id, updateData) {
    try {
      const response = await apiWithAuth.put(`${BASE_URL}/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating suggestion:', error);
      throw error;
    }
  }

  // Get dashboard statistics
  static async getDashboardStats(timeframe = '30') {
    try {
      const response = await apiWithAuth.get(`${BASE_URL}/dashboard/stats?timeframe=${timeframe}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  // Export suggestions data
  static async exportData(format = 'json', filters = {}) {
    try {
      // Filter out empty string values to avoid validation errors
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const queryParams = new URLSearchParams({
        format,
        ...cleanFilters
      });

      const response = await apiWithAuth.get(`${BASE_URL}/export/data?${queryParams}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        return response.data; // Already a blob when responseType is 'blob'
      } else {
        return response.data;
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  // Download file helper
  static downloadFile(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // Format date helper
  static formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Get status color helper
  static getStatusColor(status) {
    const colors = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'UNDER_REVIEW': 'bg-blue-100 text-blue-800',
      'APPROVED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'IMPLEMENTED': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  // Get priority color helper
  static getPriorityColor(priority) {
    const colors = {
      'LOW': 'bg-gray-100 text-gray-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'HIGH': 'bg-orange-100 text-orange-800',
      'URGENT': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  }

  // Validate suggestion data
  static validateSuggestion(data) {
    const errors = [];

    if (!data.title || data.title.trim().length === 0) {
      errors.push('Title is required');
    }

    if (!data.description || data.description.trim().length === 0) {
      errors.push('Description is required');
    }

    if (!['FEATURE', 'SHOP_ONBOARDING', 'IMPROVEMENT', 'OTHER'].includes(data.suggestionType)) {
      errors.push('Invalid suggestion type');
    }

    if (!['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'IMPLEMENTED'].includes(data.status)) {
      errors.push('Invalid status');
    }

    if (!['LOW', 'MEDIUM', 'HIGH', 'URGENT'].includes(data.priority)) {
      errors.push('Invalid priority');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default SuggestionService;